import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import QueuePanel from "@/components/QueuePanel";
import GeminiInsights from "@/components/GeminiInsights";
import DriveConnect from "@/components/DriveConnect";
import BulkQueueDialog from "@/components/BulkQueueDialog";
import BoardUpload from "@/components/BoardUpload";
import { GRADES, Pill, SubjectTabs, StatTile, SectionTitle, LessonCard, isPending, subjectMeta } from "@/components/Kit";
import MaterialViewer from "@/components/MaterialViewer";
import Markdown from "@/components/Markdown";
import SubmissionDetail from "@/components/SubmissionDetail";
import Calendar from "@/components/Calendar";
import ExamEditor from "@/components/ExamEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus, Users, FileVideo, ClipboardCheck, Loader2, Sparkles, UserPlus, Trash2, Mail,
   ScanLine, ListChecks, Video, ChevronDown, Pencil, Send, Download, Gauge, BookOpen, CalendarDays, ShieldCheck, ListOrdered,
} from "lucide-react";

function LiveNotes({ material }) {
  const [m, setM] = useState(material);
  useEffect(() => {
    setM(material);
    if (material.status === "failed" && !material.notes_md) return;
    const t = setInterval(() => api.get(`/materials/${material.id}`).then((r) => setM(r.data)).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [material]);
  const working = ["queued", "processing", "transcribing", "generating"].includes(m.status);
  return (
    <div className="rounded-2xl border border-[#1E293B] bg-[#090D16] p-6" data-testid="live-notes">
      <div className="flex items-center gap-2 text-sm mb-3">
        {working ? <Loader2 className="w-4 h-4 animate-spin text-emerald-400" /> : null}
        <span className={m.status === "failed" ? "text-rose-400" : "text-slate-300"}>
          {m.status === "failed" ? (m.error || "Processing failed.") : m.status === "queued" ? "Waiting in the queue…" : m.status === "transcribing" ? "Transcribing the recording…" : "Writing notes — they appear below as Gemini writes them"}
        </span>
      </div>
      {m.notes_md ? (
        <>
          {m.notes_partial && <div className="text-xs text-amber-300 mb-3" data-testid="partial-notes-banner">Partial notes — {working ? "still being written" : "Gemini stopped mid-way; Retry or Queue will complete them"}.</div>}
          <div className="max-h-[420px] overflow-y-auto"><Markdown>{m.notes_md}</Markdown></div>
        </>
      ) : working && <div className="text-xs text-slate-600">Cards refresh automatically.</div>}
    </div>
  );
}

function UsageMeter() {
  const [u, setU] = useState(null);
  useEffect(() => {
    const load = () => api.get("/usage").then((r) => setU(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  if (!u) return null;
  const pct = u.limit ? Math.min(100, Math.round((u.requests / u.limit) * 100)) : 0;
  const danger = u.exhausted || u.remaining <= 2;
  const color = danger ? "#F43F5E" : u.remaining <= 5 ? "#F59E0B" : "#10B981";
  const R = 34, C = 2 * Math.PI * R;
  return (
    <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5 h-full flex items-center gap-5" data-testid="usage-meter">
      <div className="relative w-20 h-20 shrink-0">
        <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
          <circle cx="40" cy="40" r={R} stroke="#1E293B" strokeWidth="7" fill="none" />
          <circle cx="40" cy="40" r={R} stroke={color} strokeWidth="7" fill="none" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C - (C * pct) / 100} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display font-extrabold text-lg leading-none" style={{ color }} data-testid="gemini-meter-counter">{u.requests}</span>
          <span className="text-[10px] text-slate-500">/ {u.limit}</span>
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2"><Gauge className="w-4 h-4 text-emerald-400" /><span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Gemini today</span></div>
        <div className="text-sm text-slate-300 mt-1.5"><span className="font-mono">{(u.tokens / 1000).toFixed(1)}k</span> tokens · <span className="font-semibold" style={{ color }}>{u.remaining}</span> requests left</div>
      <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">
        {danger
          ? "Free-tier daily limit hit. It resets at midnight Pacific — or enable billing on your Gemini key to remove the cap."
          : `Free quota is 20/day per model — ${u.models_available} of ${u.models_total} Gemini models still available today. A lesson ≈ 2 requests.`}
      </div>
      </div>
    </div>
  );
}

function AdminTeam() {
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/admins").then((r) => setAdmins(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try { await api.post("/admins", { email: email.trim() }); toast.success(`${email} authorized`); setEmail(""); load(); }
    catch (e) { toast.error("Failed to invite"); } finally { setBusy(false); }
  };
  const remove = async (em) => { try { await api.delete(`/admins/${encodeURIComponent(em)}`); load(); toast.success("Removed"); } catch (e) { toast.error("Failed"); } };
  return (
    <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-6 max-w-2xl" data-testid="admin-team-panel">
      <div className="flex items-center gap-2 mb-1"><UserPlus className="w-5 h-5 text-emerald-400" /><h2 className="text-xl font-bold font-display">Teacher Accounts</h2></div>
      <p className="text-sm text-slate-500 mb-4">Authorize email addresses that can manage lessons and exams.</p>
      <div className="flex gap-2 mb-4">
        <input data-testid="invite-admin-input" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && invite()} placeholder="teacher@school.com"
          className="flex-1 bg-[#090D16] border border-[#1E293B] text-slate-100 focus:border-emerald-500 rounded-xl px-4 py-2.5 placeholder:text-slate-600 outline-none" />
        <button data-testid="invite-admin-button" onClick={invite} disabled={busy} className="inline-flex items-center gap-2 bg-emerald-500 text-emerald-950 font-semibold rounded-xl px-5 py-2.5 hover:bg-emerald-400 transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Invite
        </button>
      </div>
      <div className="space-y-2">
        {admins.length === 0 && <div className="text-sm text-slate-600">No teachers invited yet.</div>}
        {admins.map((a) => (
          <div key={a.email} className="flex items-center justify-between p-3 rounded-xl bg-[#090D16] border border-[#1E293B]">
            <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-slate-500" /><span className="text-slate-200">{a.email}</span></div>
            <button onClick={() => remove(a.email)} className="text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Roster() {
  const [grade, setGrade] = useState("Grade 7");
  const [students, setStudents] = useState([]);
  const load = useCallback(() => { api.get(`/students?grade=${encodeURIComponent(grade)}`).then((r) => setStudents(r.data)).catch(() => {}); }, [grade]);
  useEffect(() => { load(); }, [load]);
  const changeGrade = async (uid, g) => { try { await api.put(`/students/${uid}/grade`, { grade: g }); toast.success("Grade updated"); load(); } catch (e) { toast.error("Failed"); } };
  return (
    <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-6" data-testid="roster-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><Users className="w-5 h-5 text-emerald-400" /><h2 className="text-xl font-bold font-display">Student Roster</h2></div>
        <div className="flex gap-2">{GRADES.map((g) => <Pill key={g} active={grade === g} onClick={() => setGrade(g)} testid={`roster-grade-${g}`}>{g}</Pill>)}</div>
      </div>
      <div className="space-y-2">
        {students.length === 0 && <div className="text-sm text-slate-600">No students in {grade} yet.</div>}
        {students.map((s) => (
          <div key={s.user_id} className="flex items-center justify-between p-3 rounded-xl bg-[#090D16] border border-[#1E293B]" data-testid={`roster-student-${s.user_id}`}>
            <div className="flex items-center gap-3">
              {s.picture ? <img src={s.picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" /> : <div className="w-8 h-8 rounded-full bg-[#1E293B] flex items-center justify-center text-xs">{s.name?.[0]}</div>}
              <div><div className="text-sm text-slate-100">{s.name}</div><div className="text-xs text-slate-500">{s.email}</div></div>
            </div>
            <Select value={s.grade || ""} onValueChange={(g) => changeGrade(s.user_id, g)}>
              <SelectTrigger className="w-36 bg-[#111726] border-[#1E293B] text-slate-200 h-9 text-sm" data-testid={`roster-grade-select-${s.user_id}`}><SelectValue placeholder="Set grade" /></SelectTrigger>
              <SelectContent className="bg-[#0E1420] border-[#1E293B] text-slate-100">{GRADES.map((g) => <SelectItem key={g} value={g} className="focus:bg-emerald-500/15 focus:text-emerald-300">{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}

function LessonControls({ material, onChanged, onClose }) {
  const [flags, setFlags] = useState({ email_results: !!material.email_results, email_answer_key: !!material.email_answer_key });
  const toggle = async (key, onMsg, offMsg) => {
    const v = !flags[key];
    setFlags((f) => ({ ...f, [key]: v }));
    try { await api.put(`/materials/${material.id}/flags`, { [key]: v }); toast.success(v ? onMsg : offMsg); onChanged?.(); }
    catch (e) { setFlags((f) => ({ ...f, [key]: !v })); toast.error("Could not save"); }
  };
  const Switch = ({ k, testid, label, on, off }) => (
    <label className="flex items-center gap-3 cursor-pointer text-sm text-slate-300">
      <button type="button" role="switch" aria-checked={flags[k]} data-testid={testid} onClick={() => toggle(k, on, off)}
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${flags[k] ? "bg-emerald-400" : "bg-[#334155]"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${flags[k] ? "translate-x-5" : ""}`} />
      </button>
      {label}
    </label>
  );
  const regen = async () => {
    if (!window.confirm("Regenerate notes, mindmap and formula sheet for this lesson? Uses 1 Gemini request (the saved transcript is reused).")) return;
    try { await api.post(`/materials/${material.id}/retry`); toast.success("Regenerating notes…"); onChanged?.(); onClose?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not regenerate"); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#090D16] border border-[#1E293B] p-4" data-testid="lesson-controls">
      <div className="space-y-2.5">
        <Switch k="email_results" testid="email-results-toggle" label="Email students their score + AI feedback" on="Students will get their results by email" off="Student result emails off" />
        <Switch k="email_answer_key" testid="email-answer-key-toggle" label="Email students the answer key + explanations after the test" on="Answer key will be emailed after each test" off="Answer key emails off" />
      </div>
      <button data-testid="regenerate-notes-button" onClick={regen} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-[#1E293B] text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300 transition-colors">
        <Sparkles className="w-3.5 h-3.5" /> Regenerate notes (1 request)
      </button>
    </div>
  );
}

function MaterialDialog({ material, open, onClose, onChanged }) {
  const [rubric, setRubric] = useState("");
  const [numMcq, setNumMcq] = useState(5);
  const [numSub, setNumSub] = useState(2);
  const [difficulty, setDifficulty] = useState("medium");
  const [exams, setExams] = useState([]);
  const [subs, setSubs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [genning, setGenning] = useState(false);
  const [savingRubric, setSavingRubric] = useState(false);
  const [editingExam, setEditingExam] = useState(null);

  const loadExtras = useCallback(() => {
    if (!material) return;
    api.get(`/exams?material_id=${material.id}`).then((r) => setExams(r.data)).catch(() => {});
    api.get(`/submissions?material_id=${material.id}`).then((r) => setSubs(r.data)).catch(() => {});
  }, [material]);
  useEffect(() => { if (open && material) { setRubric(material.rubric || ""); loadExtras(); } }, [open, material, loadExtras]);
  useEffect(() => {
    if (!open || !exams.some((q) => q.status === "generating")) return;
    const t = setInterval(loadExtras, 4000);
    return () => clearInterval(t);
  }, [open, exams, loadExtras]);
  if (!material) return null;

  const saveRubric = async () => { setSavingRubric(true); try { await api.put(`/materials/${material.id}/rubric`, { rubric }); toast.success("Rubric saved"); } catch (e) { toast.error("Failed"); } finally { setSavingRubric(false); } };
  const genExam = async () => {
    setGenning(true);
    try { await api.post("/exams", { material_id: material.id, num_mcq: Number(numMcq), num_subjective: Number(numSub), difficulty }); toast.success("Writing your exam in the background — it will appear here as a draft"); loadExtras(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Exam generation failed"); } finally { setGenning(false); }
  };
  const publishExam = async (eid) => { try { await api.post(`/exams/${eid}/publish`); toast.success("Published — paper emailed to admins"); loadExtras(); } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); } };
  const deleteExam = async (eid) => { try { await api.delete(`/exams/${eid}`); toast.success("Exam deleted"); loadExtras(); } catch (e) { toast.error("Failed"); } };
  const deleteMaterial = async () => {
    if (!window.confirm("Delete this recording and all its exams & submissions?")) return;
    try { await api.delete(`/materials/${material.id}`); toast.success("Recording deleted"); onChanged && onChanged(); onClose(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const ready = (material.status || "ready") === "ready";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0E1420] border-[#1E293B] text-slate-100 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="material-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="font-display text-2xl flex items-center gap-2"><FileVideo className="w-5 h-5 text-emerald-400" /> {material.title}</DialogTitle>
              <div className="text-sm text-slate-500 mt-1">{material.grade} · {material.subject}</div>
            </div>
            <button data-testid="delete-material-button" onClick={deleteMaterial} className="inline-flex items-center gap-1.5 text-sm bg-[#090D16] border border-[#1E293B] rounded-lg px-3 py-2 text-slate-400 hover:text-rose-400 hover:border-rose-500/40 transition-colors mr-8"><Trash2 className="w-4 h-4" /> Delete</button>
          </div>
        </DialogHeader>

        {!ready ? (
          <LiveNotes material={material} />
        ) : <MaterialViewer material={material} />}
        {ready && material.miro_status && <div className="text-xs text-slate-500" data-testid="miro-status">Whiteboard: {material.miro_status}</div>}
        {ready && <LessonControls material={material} onChanged={onChanged} onClose={onClose} />}

        <div className="grid md:grid-cols-2 gap-4 mt-2">
          <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3"><ListChecks className="w-4 h-4 text-indigo-400" /><h3 className="font-semibold">Create Exam</h3></div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <label className="text-sm text-slate-400 flex flex-col gap-1">MCQ (max 20)<input data-testid="num-mcq-input" type="number" min="0" max="20" value={numMcq} onChange={(e) => setNumMcq(e.target.value)} className="bg-[#090D16] border border-[#1E293B] rounded-lg px-2 py-1.5 text-slate-100 outline-none focus:border-emerald-500" /></label>
              <label className="text-sm text-slate-400 flex flex-col gap-1">Written / numerical (max 15)<input data-testid="num-subjective-input" type="number" min="0" max="15" value={numSub} onChange={(e) => setNumSub(e.target.value)} className="bg-[#090D16] border border-[#1E293B] rounded-lg px-2 py-1.5 text-slate-100 outline-none focus:border-emerald-500" /></label>
              <label className="text-sm text-slate-400 flex flex-col gap-1">Level<select data-testid="difficulty-select" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="bg-[#090D16] border border-[#1E293B] rounded-lg px-2 py-2 text-slate-100 outline-none focus:border-emerald-500"><option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option><option value="jee">JEE level</option><option value="mixed">Mixed</option></select></label>
            </div>
            <button data-testid="generate-exam-button" onClick={genExam} disabled={genning || !ready} className="w-full inline-flex items-center justify-center gap-1.5 bg-indigo-500 text-white font-medium rounded-lg px-3 py-2 text-sm hover:bg-indigo-400 transition-colors disabled:opacity-50">
              {genning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate Draft
            </button>
            <div className="space-y-2 text-sm mt-3">
              {exams.map((q) => (
                <div key={q.id} className="p-2.5 rounded-lg bg-[#090D16] border border-[#1E293B]" data-testid={`exam-row-${q.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-300">{q.num_mcq} MCQ · {q.num_subjective} written · <span className="capitalize text-slate-500">{q.difficulty || "medium"}</span></span>
                    <span data-testid={`exam-status-${q.id}`} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${q.status === "published" ? "bg-emerald-500/15 text-emerald-400" : q.status === "generating" ? "bg-amber-500/15 text-amber-300" : q.status === "failed" ? "bg-rose-500/15 text-rose-300" : "bg-slate-500/15 text-slate-400"}`}>
                      {q.status === "generating" && <Loader2 className="w-3 h-3 animate-spin" />}
                      {q.status === "published" ? "Published" : q.status === "generating" ? "Writing…" : q.status === "failed" ? "Failed" : "Draft"}
                    </span>
                  </div>
                  {q.status === "failed" && <div className="text-xs text-rose-300 mb-2" data-testid={`exam-error-${q.id}`}>{q.error}</div>}
                  {q.status === "generating" ? <div className="text-xs text-slate-500">Gemini is writing the questions — you can close this window.</div> : (
                  <div className="flex flex-wrap gap-1.5">
                    {q.status !== "failed" && <><button data-testid={`edit-exam-${q.id}`} onClick={() => setEditingExam(q.id)} className="inline-flex items-center gap-1 text-xs bg-[#111726] border border-[#1E293B] rounded px-2 py-1 text-slate-300 hover:border-emerald-500/50"><Pencil className="w-3 h-3" /> Edit</button>
                    {q.status !== "published" && <button data-testid={`publish-exam-${q.id}`} onClick={() => publishExam(q.id)} className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 border border-emerald-500/40 rounded px-2 py-1 text-emerald-300 hover:bg-emerald-500/25"><Send className="w-3 h-3" /> Publish</button>}
                    <a href={`${API}/exams/${q.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs bg-[#111726] border border-[#1E293B] rounded px-2 py-1 text-slate-300 hover:border-emerald-500/50"><Download className="w-3 h-3" /> PDF</a></>}
                    <button data-testid={`delete-exam-${q.id}`} onClick={() => deleteExam(q.id)} className="inline-flex items-center gap-1 text-xs bg-[#111726] border border-[#1E293B] rounded px-2 py-1 text-slate-400 hover:text-rose-400 hover:border-rose-500/40"><Trash2 className="w-3 h-3" /></button>
                  </div>)}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3"><ScanLine className="w-4 h-4 text-amber-400" /><h3 className="font-semibold">Homework Rubric</h3></div>
            <textarea data-testid="rubric-input" value={rubric} onChange={(e) => setRubric(e.target.value)} rows={4} placeholder="e.g. Correct method (10) + correct final answer (10). Deduct for missing steps."
              className="w-full bg-[#090D16] border border-[#1E293B] rounded-xl px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-emerald-500 resize-none placeholder:text-slate-600" />
            <button data-testid="save-rubric-button" onClick={saveRubric} disabled={savingRubric} className="mt-2 inline-flex items-center gap-1.5 bg-amber-500 text-amber-950 font-medium rounded-lg px-4 py-1.5 text-sm hover:bg-amber-400 transition-colors disabled:opacity-50">
              {savingRubric && <Loader2 className="w-4 h-4 animate-spin" />} Save Rubric
            </button>
          </div>
        </div>

        <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5 mt-2">
          <div className="flex items-center gap-2 mb-3"><ClipboardCheck className="w-4 h-4 text-emerald-400" /><h3 className="font-semibold">Submissions ({subs.length})</h3></div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {subs.length === 0 && <div className="text-sm text-slate-600">No submissions yet.</div>}
            {subs.map((s) => (
              <div key={s.id} className="rounded-xl bg-[#090D16] border border-[#1E293B]" data-testid={`admin-submission-${s.id}`}>
                <button onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="w-full flex items-center justify-between p-3 text-left">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${s.type === "exam" ? "bg-indigo-500/15 text-indigo-400" : "bg-amber-500/15 text-amber-400"}`}>{s.type === "exam" ? "Exam" : "Homework"}</span>
                    <span className="text-sm text-slate-300">{s.student_name || s.student_email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-emerald-400">{s.percentage}%</span>
                    <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${expanded === s.id ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {expanded === s.id && <div className="px-3 pb-3"><SubmissionDetail submission={s} /></div>}
              </div>
            ))}
          </div>
        </div>

        <ExamEditor examId={editingExam} open={!!editingExam} onClose={() => setEditingExam(null)} onChanged={loadExtras} />
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("content");
  const [grade, setGrade] = useState("Grade 7");
  const [subject, setSubject] = useState("Physics");
  const [materials, setMaterials] = useState([]);
  const [stats, setStats] = useState({ students: 0, materials: 0, exams: 0, submissions: 0 });
  const [openMaterial, setOpenMaterial] = useState(null);
  const [mTitle, setMTitle] = useState("");
  const [mUrl, setMUrl] = useState("");
  const [mMiro, setMMiro] = useState("");
  const [mTranscript, setMTranscript] = useState("");
  const [uploading, setUploading] = useState(false);

  const loadStats = useCallback(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const loadMaterials = useCallback(() => {
    api.get(`/materials?grade=${encodeURIComponent(grade)}&subject=${encodeURIComponent(subject)}`).then((r) => setMaterials(r.data)).catch(() => {});
  }, [grade, subject]);
  useEffect(() => { loadMaterials(); }, [loadMaterials]);
  useEffect(() => {
    if (!materials.some(isPending)) return;
    const t = setInterval(loadMaterials, 4000);
    return () => clearInterval(t);
  }, [materials, loadMaterials]);

  const addMaterial = async (queue = false) => {
    if (!mTitle.trim()) { toast.error("Enter a lesson title"); return; }
    if (!mUrl.trim() && !mTranscript.trim()) { toast.error("Add a recording link or paste a transcript"); return; }
    setUploading(queue ? "queue" : "now");
    try {
      await api.post("/materials", { grade, subject, title: mTitle.trim(), video_url: mUrl.trim(), miro_url: mMiro.trim(), transcript: mTranscript.trim(), queue, board_paths: boards.map((b) => b.path) });
      toast.success(queue ? "Added to queue — it will be ready within ~6 hours" : mTranscript.trim() ? "Generating notes & mindmap…" : "Listening to your recording…");
      setMTitle(""); setMUrl(""); setMMiro(""); setMTranscript(""); setBoards([]); loadMaterials(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); } finally { setUploading(false); }
  };
  const [boards, setBoards] = useState([]);
  const queueIt = async (id) => {
    try { await api.post(`/materials/${id}/queue`); toast.success("Added to queue"); loadMaterials(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not queue"); }
  };
  const retry = async (id) => {
    try { await api.post(`/materials/${id}/retry`); toast.success("Retrying…"); loadMaterials(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
  };

  const nav = [
    { id: "content", label: "Lessons", icon: BookOpen },
    { id: "queue", label: "Queue", icon: ListOrdered },
    { id: "calendar", label: "Calendar", icon: CalendarDays },
    { id: "roster", label: "Students", icon: Users },
  ];
  if (user?.role === "super_admin") nav.push({ id: "admins", label: "Teachers", icon: ShieldCheck });
  const subj = subjectMeta(subject);
  const inputCls = "w-full bg-[#090D16] border border-[#1E293B] rounded-xl px-4 py-3 text-sm text-slate-100 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-[border-color,box-shadow] placeholder:text-slate-600";

  return (
    <AppShell nav={nav} active={tab} onNav={setTab}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-400">Teacher workspace</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display tracking-tight mt-2">Hello, {user?.name?.split(" ")[0] || "Teacher"}.</h1>
        <p className="text-slate-400 mt-2 text-sm sm:text-base">Turn today's recordings into notes, mindmaps and exams.</p>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-12 gap-4 mb-10">
        <div className="col-span-1 lg:col-span-2"><StatTile icon={Users} label="Students" value={stats.students} color="#818CF8" /></div>
        <div className="col-span-1 lg:col-span-2"><StatTile icon={FileVideo} label="Lessons" value={stats.materials} color="#22D3EE" /></div>
        <div className="col-span-1 lg:col-span-2"><StatTile icon={ListChecks} label="Exams" value={stats.exams} color="#F59E0B" /></div>
        <div className="col-span-1 lg:col-span-2"><StatTile icon={ClipboardCheck} label="Submissions" value={stats.submissions} color="#10B981" /></div>
        <div className="col-span-2 lg:col-span-4"><UsageMeter /></div>
      </div>

      {tab === "admins" && <AdminTeam />}
      {tab === "roster" && <Roster />}
      {tab === "calendar" && <Calendar />}
      {tab === "queue" && <><DriveConnect /><GeminiInsights /><QueuePanel /></>}

      {tab === "content" && (
        <>
          <SectionTitle overline="Classroom" title={`${grade} · ${subject}`}
            right={<div className="flex flex-wrap gap-2">{GRADES.map((g) => <Pill key={g} active={grade === g} onClick={() => setGrade(g)} testid={`grade-pill-${g}`}>{g}</Pill>)}</div>} />
          <div className="mb-8"><SubjectTabs value={subject} onChange={setSubject} /></div>

          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4">
              <div className="bg-[#111726] border border-[#1E293B] rounded-2xl overflow-hidden lg:sticky lg:top-8" data-testid="new-lesson-panel">
                <div className="relative h-24">
                  <img src={subj.img} alt="" className="w-full h-full object-cover opacity-50" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111726] to-transparent" />
                  <div className="absolute bottom-3 left-5 flex items-center gap-2"><Video className="w-5 h-5 text-emerald-400" /><h3 className="text-lg font-bold font-display">New lesson</h3></div>
                </div>
                <div className="p-5 space-y-3">
                  <p className="text-xs text-slate-500 leading-relaxed">Paste a Google Drive recording link. Gemini listens to the audio and writes notes + a mindmap. Sharing must be "Anyone with the link".</p>
                  <input data-testid="material-title-input" value={mTitle} onChange={(e) => setMTitle(e.target.value)} placeholder="Lesson title" className={inputCls} />
                  <input data-testid="material-url-input" value={mUrl} onChange={(e) => setMUrl(e.target.value)} placeholder="https://drive.google.com/file/d/…" className={inputCls} />
                  <input data-testid="material-miro-input" value={mMiro} onChange={(e) => setMMiro(e.target.value)} placeholder="Miro board link (optional)" className={inputCls} />
                  <BoardUpload files={boards} onChange={setBoards} />
                  <textarea data-testid="material-transcript-input" value={mTranscript} onChange={(e) => setMTranscript(e.target.value)} rows={3} placeholder="Optional: paste your own transcript (saves 1 request)" className={`${inputCls} resize-none`} />
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="generate-material-button" onClick={() => addMaterial(false)} disabled={!!uploading} className="inline-flex items-center justify-center gap-2 bg-emerald-400 text-slate-950 font-bold rounded-full px-4 py-3 text-sm hover:bg-emerald-300 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50">
                      {uploading === "now" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate now
                    </button>
                    <button data-testid="queue-material-button" onClick={() => addMaterial(true)} disabled={!!uploading} className="inline-flex items-center justify-center gap-2 bg-indigo-500/15 border border-indigo-400/40 text-indigo-200 font-bold rounded-full px-4 py-3 text-sm hover:bg-indigo-500/25 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50">
                      {uploading === "queue" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListOrdered className="w-4 h-4" />} Queue for later
                    </button>
                  </div>
                  <BulkQueueDialog grade={grade} subject={subject} onDone={() => { loadMaterials(); loadStats(); }} />
                  <p className="text-[11px] text-slate-600 leading-relaxed">Queue: runs automatically when Gemini is free — ready within ~6 hours (needs free quota or billing).</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="grid sm:grid-cols-2 gap-5">
                {materials.length === 0 && (
                  <div className="sm:col-span-2 rounded-2xl border border-dashed border-[#1E293B] py-16 text-center" data-testid="empty-lessons">
                    <subj.icon className="w-8 h-8 mx-auto mb-3" style={{ color: subj.color }} />
                    <div className="text-slate-300 font-semibold">No lessons in {grade} {subject} yet</div>
                    <div className="text-sm text-slate-500 mt-1">Add your first recording on the left.</div>
                  </div>
                )}
                {materials.map((m) => (
                  <LessonCard key={m.id} m={m} testid={`material-card-${m.id}`} onOpen={() => setOpenMaterial(m)} onRetry={() => retry(m.id)} onQueue={() => queueIt(m.id)} />
                ))}
              </div>
            </div>
          </div>
        </>
      )}
      <MaterialDialog material={openMaterial} open={!!openMaterial} onClose={() => setOpenMaterial(null)} onChanged={() => { loadMaterials(); loadStats(); }} />
    </AppShell>
  );
}
